/**
 * Seeker invite flow — client helpers (demand side).
 *
 * Mirror of claimFlow.ts, kept standalone so edits to the shared shell (App.tsx,
 * OnboardingRouter) stay minimal + additive — cheap to rebase on Maciek's main.
 * See [[flat_hero_collaboration_model]].
 *
 * Flow: a /dla-ciebie/:token landing page (scraped seeker reached via mock FB
 * outreach) stashes the token (SeekerCapture, mirrors ClaimCapture) → the visitor
 * registers via Maciek's normal signup → after SMS-verify, OnboardingRouter calls
 * match_seeker_invite (runs as auth.uid()): links the account to the scraped seeker
 * AND pre-fills their tenant search criteria. The seeker then continues Maciek's
 * onboarding unchanged (no reroute).
 */

import { supabase } from '@/integrations/supabase/client';
import { clearFlowToken, readFlowToken, stashFlowToken } from '@/lib/flowToken';

const SEEKER_TOKEN_KEY = 'flathero_seeker_token';

/** A seeker token is 36 hex chars (`encode(gen_random_bytes(18),'hex')`). */
export function isSeekerToken(token: string | null | undefined): token is string {
  return !!token && /^[0-9a-f]{36}$/i.test(token);
}

/** Bounded by FLOW_TOKEN_TTL_MS — see flowToken.ts. Restarts the clock. */
export function stashSeekerToken(token: string): void {
  if (isSeekerToken(token)) stashFlowToken(SEEKER_TOKEN_KEY, token);
}

export function getPendingSeekerToken(): string | null {
  return readFlowToken(SEEKER_TOKEN_KEY, isSeekerToken);
}

export function clearPendingSeekerToken(): void {
  clearFlowToken(SEEKER_TOKEN_KEY);
}

export interface SeekerRedeemResult {
  redeemed: boolean;
  alreadyMatched?: boolean;
  sourcePostId?: string | null;
  error?: string;
}

/**
 * Redeem a pending seeker invite for the currently-authenticated user. Calls the
 * SECURITY DEFINER RPC `flat.match_seeker_invite(p_token)` which keys off auth.uid()
 * (marks the invite matched + best-effort pre-fills the tenant's criteria rows).
 * Clears the token on a terminal outcome so we don't loop; keeps it on a transient
 * error so a later attempt can retry. Returns {redeemed:false} with no token present.
 */
export async function redeemPendingSeeker(): Promise<SeekerRedeemResult> {
  const token = getPendingSeekerToken();
  if (!token) return { redeemed: false };

  // flat schema is not in the generated Supabase types — opt out of typing
  // (same `as never` escape hatch used by the flat-schema hooks).
  const { data, error } = await supabase
    .schema('flat' as never)
    .rpc('match_seeker_invite' as never, { p_token: token } as never);

  if (error) return { redeemed: false, error: error.message };

  const r = (data ?? {}) as {
    ok?: boolean; error?: string; matched?: boolean;
    already_matched?: boolean; source_post_id?: string | null;
  };

  if (r.ok) {
    clearPendingSeekerToken();
    return {
      redeemed: true,
      alreadyMatched: !!r.already_matched,
      sourcePostId: r.source_post_id ?? null,
    };
  }

  // Terminal server-side rejection (invalid/used token) — drop it; keep on auth race.
  if (r.error && r.error !== 'not_authenticated') clearPendingSeekerToken();
  return { redeemed: false, error: r.error };
}
